import { useEffect, useMemo, useState } from "react";
import { CartesianGrid, Line, LineChart, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useT } from "@/lib/i18n";
import { deltaPoints, mergeLaps, posAt } from "@/lib/telemetry";
import { cursorProps, onCursor } from "@/lib/cursor";

const tick = { fill: "#94A3B8", fontSize: 10, fontFamily: "JetBrains Mono" };
const fmtVal = (v) => (typeof v !== "number" ? v : Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(2));
const fmtX = (axis) => (x) => (axis === "t" ? `${Number(x).toFixed(2)} s` : `${Math.round(x)} m`);
const lapName = (lap, t) => lap.label || `${t("lap_col")} ${lap.lap}`;

const CompactTip = ({ active, payload, label, axis, fmt = fmtVal }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#090A0F]/90 border border-[#282D3D] rounded-sm px-1.5 py-0.5 font-mono text-[10px] leading-tight pointer-events-none">
      <div className="text-slate-500">{fmtX(axis)(label)}</div>
      {payload.filter((p) => p.value != null).map((p) => <div key={p.dataKey} style={{ color: p.stroke }}>{fmt(p.value)}</div>)}
    </div>
  );
};

const Frame = ({ title, testid, children, h = 200, onReset }) => (
  <div data-testid={testid} onDoubleClick={onReset} className="bg-[#12141C] border border-[#282D3D] rounded-sm p-3 select-none">
    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1 px-1">{title}</div>
    <div style={{ height: h }} className="cursor-crosshair">{children}</div>
  </div>
);

const xAxis = (axis, zoom) => (
  <XAxis dataKey="x" type="number" domain={zoom || [0, "dataMax"]} allowDataOverflow tick={tick} stroke="#282D3D" unit={axis === "t" ? "s" : "m"}
    tickFormatter={(v) => (zoom && zoom[1] - zoom[0] < 20 ? Number(v).toFixed(1) : Math.round(v))} />
);

function useZoomDrag(data, onZoom) {
  const [sel, setSel] = useState(null);
  const xOf = (s) => {
    const i = s?.activeTooltipIndex;
    return i != null && i !== "" ? data[Number(i)]?.x : null;
  };
  const cur = cursorProps(data);
  const handlers = {
    onMouseDown: (s) => { const x = xOf(s); if (x != null) setSel([x, x]); },
    onMouseMove: (s) => { cur.onMouseMove(s); const x = xOf(s); if (sel && x != null) setSel([sel[0], x]); },
    onMouseUp: () => {
      if (sel && Math.abs(sel[1] - sel[0]) > 0) onZoom([Math.min(...sel), Math.max(...sel)]);
      setSel(null);
    },
    onMouseLeave: () => { cur.onMouseLeave(); setSel(null); },
  };
  const area = sel && sel[0] !== sel[1] ? <ReferenceArea x1={sel[0]} x2={sel[1]} fill="#FFB800" fillOpacity={0.12} stroke="#FFB800" strokeOpacity={0.5} /> : null;
  return { handlers, area };
}
const sectorLines = (marks, axis) => marks.map((m) => (
  <ReferenceLine key={m.label} x={m[axis]} stroke="#FFB800" strokeDasharray="4 3" strokeOpacity={0.55} label={{ value: m.label, position: "insideTopRight", fill: "#FFB800", fontSize: 10 }} />
));

export const C_COLOR = "#22D3EE";

export const ChannelChart = ({ channel, lapA, lapB, lapC, grid, axis, marks, h = 200, zoom, onZoom }) => {
  const { t } = useT();
  const data = useMemo(() => mergeLaps(lapA, lapB, channel, grid, axis, lapC), [lapA, lapB, lapC, channel, grid, axis]);
  const drag = useZoomDrag(data, onZoom);
  return (
    <Frame title={channel} testid={`telemetry-chart-${channel}`} h={h} onReset={() => onZoom(null)}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} syncId="telemetry" syncMethod="value" margin={{ top: 5, right: 10, bottom: 0, left: 0 }} {...drag.handlers}>
          <CartesianGrid stroke="#282D3D" strokeDasharray="3 3" />
          {xAxis(axis, zoom)}
          <YAxis domain={["auto", "auto"]} tick={tick} stroke="#282D3D" width={48} />
          {sectorLines(marks, axis)}
          <Tooltip content={<CompactTip axis={axis} />} cursor={{ stroke: "#64748B" }} offset={12} isAnimationActive={false} />
          <Line dataKey="a" name={lapName(lapA, t)} stroke="#007AFF" dot={false} strokeWidth={1.6} isAnimationActive={false} />
          {lapB && <Line dataKey="b" name={lapName(lapB, t)} stroke="#FF3B30" dot={false} strokeWidth={1.4} isAnimationActive={false} />}
          {lapC && <Line dataKey="c" name={lapName(lapC, t)} stroke={C_COLOR} dot={false} strokeWidth={1.4} isAnimationActive={false} />}
          {drag.area}
        </LineChart>
      </ResponsiveContainer>
    </Frame>
  );
};

export const DeltaChart = ({ lapA, lapB, lapC, axis, marks, h = 170, zoom, onZoom }) => {
  const { t } = useT();
  const data = useMemo(() => {
    const pts = deltaPoints(lapA, lapB, axis, lapC);
    if (!zoom) return pts;
    const i0 = Math.max(0, pts.findIndex((p) => p.x >= zoom[0]) - 1);
    const i1 = pts.findIndex((p) => p.x > zoom[1]);
    return pts.slice(i0, i1 < 0 ? pts.length : i1 + 1);
  }, [lapA, lapB, lapC, axis, zoom]);
  const drag = useZoomDrag(data, onZoom);
  return (
    <Frame title={t(lapC ? "delta_time_multi" : "delta_time")} testid="telemetry-delta-chart" h={h} onReset={() => onZoom(null)}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} syncId="telemetry" syncMethod="value" margin={{ top: 5, right: 10, bottom: 0, left: 0 }} {...drag.handlers}>
          <CartesianGrid stroke="#282D3D" strokeDasharray="3 3" />
          {xAxis(axis, zoom)}
          <YAxis domain={["auto", "auto"]} tick={tick} stroke="#282D3D" width={48} unit="s" />
          <ReferenceLine y={0} stroke="#64748B" />
          {sectorLines(marks, axis)}
          <Tooltip content={<CompactTip axis={axis} fmt={(v) => `${v > 0 ? "+" : ""}${v} s`} />} cursor={{ stroke: "#64748B" }} offset={12} isAnimationActive={false} />
          {lapB && <Line dataKey="delta" stroke="#FF3B30" dot={false} strokeWidth={1.8} isAnimationActive={false} />}
          {lapC && <Line dataKey="deltaC" stroke={C_COLOR} dot={false} strokeWidth={1.6} isAnimationActive={false} />}
          {drag.area}
        </LineChart>
      </ResponsiveContainer>
    </Frame>
  );
};

const xyOf = (lap) => (lap?.x?.length ? lap.x.map((x, i) => [x, lap.y[i]]) : null);

export const TraceSvg = ({ lap, lapB, lapC, colors, axis }) => {
  const [cur, setCur] = useState(null);
  useEffect(() => onCursor(setCur), []);
  const xyA = useMemo(() => xyOf(lap), [lap]);
  const xyB = useMemo(() => xyOf(lapB), [lapB]);
  const xyC = useMemo(() => xyOf(lapC), [lapC]);
  if (!xyA) return null;
  const xs = lap.x, ys = lap.y;
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const pad = 10, w = maxX - minX || 1, h = maxY - minY || 1, r = Math.max(w, h) / 45;
  const dots = [[posAt(lap, xyA, cur, axis), "#007AFF", "a"], [lapB && posAt(lapB, xyB, cur, axis), "#FF3B30", "b"], [lapC && posAt(lapC, xyC, cur, axis), C_COLOR, "c"]];
  return (
    <svg data-testid="telemetry-trace-svg" viewBox={`${minX - pad} ${-maxY - pad} ${w + pad * 2} ${h + pad * 2}`} className="w-full h-full">
      {xs.slice(1).map((x, i) => (
        <line key={i} x1={xs[i]} y1={-ys[i]} x2={x} y2={-ys[i + 1]} strokeWidth={Math.max(w, h) / 90} strokeLinecap="round" stroke={colors[i]} />
      ))}
      <circle cx={xs[0]} cy={-ys[0]} r={Math.max(w, h) / 60} fill="#fff" />
      {dots.map(([p, c, k]) => p && <circle key={k} data-testid={`trace-cursor-${k}`} cx={p[0]} cy={-p[1]} r={r} fill={c} stroke="#fff" strokeWidth={r / 4} />)}
    </svg>
  );
};
